import type { HouseView, Person, ScoreboardView, SessionInfo, TodayView, Trip } from "../shared/types.ts";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
  ) {
    super(`${status} ${code}`);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: init?.body ? { "content-type": "application/json", ...init?.headers } : init?.headers,
    credentials: "same-origin",
  });

  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { error?: string };
    throw new ApiError(response.status, body.error ?? "request_failed");
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

const json = (body: unknown): RequestInit => ({ method: "POST", body: JSON.stringify(body) });
const patch = (body: unknown): RequestInit => ({ method: "PATCH", body: JSON.stringify(body) });

export const api = {
  me: () => request<SessionInfo>("/api/me"),
  people: () => request<Person[]>("/api/people"),
  house: () => request<HouseView>("/api/house"),
  today: (kidOnly?: boolean) => request<TodayView>(`/api/today${kidOnly ? "?kid=1" : ""}`),
  scoreboard: (weeksAgo = 0) => request<ScoreboardView>(`/api/scoreboard?weeksAgo=${weeksAgo}`),

  complete: (taskId: number, personId?: number | null) =>
    request<{ ok: true; completionId: number; points: number }>(
      `/api/tasks/${taskId}/complete`,
      json({ personId: personId ?? undefined }),
    ),
  undo: (taskId: number) =>
    request<{ ok: true; personId: number | null; doneAt: string }>(`/api/tasks/${taskId}/undo`, { method: "POST" }),
  undoCompletion: (completionId: number) =>
    request<{ ok: true; taskId: number; personId: number | null }>(`/api/completions/${completionId}`, {
      method: "DELETE",
    }),

  kidLogin: (pin: string, personId: number) => request<{ ok: true }>("/auth/kid", json({ pin, personId })),
  logout: () => request<{ ok: true }>("/auth/logout", { method: "POST" }),

  createFloor: (name: string) => request("/api/floors", json({ name })),
  updateFloor: (id: number, body: Record<string, unknown>) => request(`/api/floors/${id}`, patch(body)),
  deleteFloor: (id: number) => request(`/api/floors/${id}`, { method: "DELETE" }),

  createRoom: (body: { floorId: number; name: string; emoji?: string }) => request("/api/rooms", json(body)),
  updateRoom: (id: number, body: Record<string, unknown>) => request(`/api/rooms/${id}`, patch(body)),
  deleteRoom: (id: number) => request(`/api/rooms/${id}`, { method: "DELETE" }),

  createTask: (body: Record<string, unknown>) => request("/api/tasks", json(body)),
  updateTask: (id: number, body: Record<string, unknown>) => request(`/api/tasks/${id}`, patch(body)),
  archiveTask: (id: number) => request(`/api/tasks/${id}`, { method: "DELETE" }),

  updatePerson: (id: number, body: Record<string, unknown>) => request(`/api/people/${id}`, patch(body)),
  setKidPin: (id: number, pin: string) => request<{ ok: true; name: string }>(`/api/people/${id}/pin`, json({ pin })),

  trips: () => request<Trip[]>("/api/trips"),
  createTrip: (body: { name: string; startsOn: string; endsOn: string; emoji?: string }) =>
    request<Trip>("/api/trips", json(body)),
  updateTrip: (id: number, body: Record<string, unknown>) => request<Trip>(`/api/trips/${id}`, patch(body)),
  deleteTrip: (id: number) => request(`/api/trips/${id}`, { method: "DELETE" }),
};
